import { useLazyQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Order } from '@rebound/shared-models/order/order.model';
import { UserJourneyEnum } from '@rebound/shared-models/retailer/retailer.model';

import {
  decryptSearchOrderMutation,
  decryptSearchOrderResult,
} from '@modules/auth/auth.queries';
import OrderDetailsModal from '@modules/dashboard/components/OrderDetailsModal';
import OrderLookupForm from '@modules/dashboard/components/OrderLookupForm';
import {
  CreateDraftReturnMutation,
  CreateDraftReturnMutationResult,
  GetUnfinishedDraftReturnQuery,
  GetUnfinishedDraftReturnQueryResult,
} from '@modules/draft-return/draft-return.queries';
import { useRetailerData } from '@modules/retailer/retailer.context';
import { QueryParams } from '@modules/return-journey/return-journey.model';
import { anyItemWithinReturnWindow } from '@modules/return-journey/return-journey.utils';

import { LandingPage } from '../components/LandingPage';
import { localesOptions } from '../landing.utils';

const SearchOrderPage = () => {
  const { t } = useTranslation();
  const retailerData = useRetailerData();
  const router = useRouter();
  const [foundOrder, setFoundOrder] = useState<Order | null>(null);
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [modalVisible, setModalVisible] = useState({ value: false });
  const isItemReturnedInitialRender = useRef(true);
  const [localOrderReference, setLocalOrderReference] = useState('');
  const [localPostalCode, setLocalPostalCode] = useState('');

  const queryParams: QueryParams = {
    orderReference: router.query.orderReference as string,
    email: router.query.email as string,
    postalCode: router.query.postalCode as string,
    lang: router.query.lang as string,
  };

  const changeLanguage = (lang: string) => {
    i18next.changeLanguage(lang);
  };

  const DEFAULT_LANGUAGE = 'en';

  const returnAvailable =
    retailerData.retailer &&
    !!foundOrder?.items.find((i) => !i.nonReturnable) &&
    (anyItemWithinReturnWindow(
      foundOrder,
      retailerData.retailer.returnPolicyDays,
    ) ||
      Boolean(foundOrder.rma));

  const [decryptSearchOrder, { loading: decryptionLoading }] = useMutation(
    decryptSearchOrderMutation,
    {
      onCompleted: (data: decryptSearchOrderResult) => {
        if (data.decryptSearchOrder) {
          setEmail(data.decryptSearchOrder.email);
        }
      },
    },
  );

  const [createDraftReturn] = useMutation<CreateDraftReturnMutationResult>(
    CreateDraftReturnMutation,
    {
      onCompleted: (data) => {
        (window as any).heap.track('Create return', data.createDraftReturn);

        router.push(`/return/${data.createDraftReturn._id}/select-items`);
      },
      onError: () => {
        const message = t('errors.somethingWentWrong');
        toast.error(message);
      },
    },
  );

  const [getUnfinishedDraftReturn] =
    useLazyQuery<GetUnfinishedDraftReturnQueryResult>(
      GetUnfinishedDraftReturnQuery,
      {
        variables: {
          orderReference: foundOrder?.id,
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
          const unfinishedDraftReturn = data.getUnfinishedDraftReturn;
          if (unfinishedDraftReturn && returnAvailable) {
            const continueReturnUrl =
              unfinishedDraftReturn &&
              unfinishedDraftReturn._id &&
              unfinishedDraftReturn.currentStepSlug
                ? `/return/${
                    unfinishedDraftReturn._id
                  }/${unfinishedDraftReturn.currentStepSlug.replace(
                    /['"]/g,
                    '',
                  )}`
                : '/';
            router.push(continueReturnUrl);
          } else if (!unfinishedDraftReturn && returnAvailable) {
            createDraftReturn({
              variables: {
                orderId: foundOrder?.id,
                identifier: foundOrder?.zip || foundOrder?.email,
              },
            });
          } else {
            setModalVisible({ value: true });
          }
        },
      },
    );

  useEffect(() => {
    decryptSearchOrder({
      variables: {
        decryptionParams: {
          email: queryParams.email,
        },
      },
    });
  }, []);

  useEffect(() => {
    if (queryParams.lang && localesOptions[queryParams.lang]) {
      changeLanguage(queryParams.lang);
    } else {
      changeLanguage(DEFAULT_LANGUAGE);
    }
  }, [queryParams.lang]);

  useEffect(() => {
    let anyItemReturned = false;
    let anyItemNonReturnable = false;

    if (foundOrder && foundOrder.items) {
      foundOrder.items.forEach((item) => {
        if (item.returned) {
          anyItemReturned = true;
        }
        if (item.nonReturnable) {
          anyItemNonReturnable = true;
        }
      });

      if (anyItemReturned || anyItemNonReturnable || !returnAvailable) {
        setModalVisible({ value: true });
      } else {
        getUnfinishedDraftReturn();
      }
    }
  }, [foundOrder]);

  useEffect(() => {
    if (isItemReturnedInitialRender.current) {
      isItemReturnedInitialRender.current = false;
    } else {
      if (modalVisible) {
        setVisible(true);
      }
    }
  }, [modalVisible]);

  useEffect(() => {
    const order = {
      reference: localStorage.getItem('orderReference'),
      postalCode: localStorage.getItem('postalCode'),
    };
    // redirected from track-return page
    if (order.reference && !queryParams.orderReference) {
      setLocalOrderReference(order.reference);
    }
    if (order.postalCode && !queryParams.postalCode) {
      setLocalPostalCode(order.postalCode);
    }
  }, [queryParams.postalCode, queryParams.orderReference]);

  const hasAutoSearch =
    retailerData.retailer?.userJourneyRadio === UserJourneyEnum.ORDER_LOOKUP &&
    !!(
      queryParams.orderReference ||
      email ||
      queryParams.postalCode ||
      (localOrderReference && localPostalCode)
    );

  const orderLookUpForm = (
    <OrderLookupForm
      orderReference={queryParams.orderReference || localOrderReference}
      emailOrPostalCode={
        (email ? email : queryParams.postalCode) || localPostalCode
      }
      autoSearch={hasAutoSearch}
      setFoundOrder={setFoundOrder}
    />
  );

  return (
    <>
      <LandingPage
        retailerData={retailerData}
        decryptionLoading={decryptionLoading}
        form={orderLookUpForm}
        title={t('orderLookup.titleOne')}
        description={t('orderLookup.descriptionOne')}
        searchOrderJourney={true}
      />
      <Wrapper>
        {foundOrder && modalVisible && (
          <OrderDetailsModal
            visible={visible}
            setVisible={setVisible}
            order={foundOrder}
            setFoundOrder={setFoundOrder}
          />
        )}
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    font-family: 'Inter', sans-serif;
  }
`;

export default SearchOrderPage;
